/**
 * FloatingActions Component
 * 
 * Container for all floating action buttons (Orders, Loyalty)
 */

import React, { useState, useEffect, useRef } from 'react';
import { Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { OrderHistoryDialog } from '@/components/OrderHistoryDialog';
import { CustomerLoyaltyWidget } from '@/components/CustomerLoyaltyWidget';

interface FloatingActionsProps {
  branchId: string;
  restaurantId: string;
  currency: string;
  enableOrdering?: boolean;
  isPreview?: boolean;
  className?: string;
}

export function FloatingActions({
  branchId,
  restaurantId,
  currency,
  enableOrdering = true,
  isPreview = false,
  className = '',
}: FloatingActionsProps) {
  const { i18n } = useTranslation();
  const [showFloatingButtons, setShowFloatingButtons] = useState(true);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);

  useEffect(() => {
    function handleScroll() {
      if (!ticking.current) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          // Hide buttons if scrolled more than 120px from top
          setShowFloatingButtons(currentScrollY < 120);
          lastScrollY.current = currentScrollY;
          ticking.current = false;
        });
        ticking.current = true;
      }
    }
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {/* Floating My Orders Button */}
      <div
        className={`${isPreview ? 'absolute' : 'fixed'} top-4 left-4 z-20 transition-opacity duration-300 ${
          showFloatingButtons ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        } ${className}`}
      >
        <OrderHistoryDialog
          branchId={branchId}
          currency={currency}
          trigger={
            <button className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300 hover:scale-105 backdrop-blur-sm">
              <Clock className="w-4 h-4 text-gray-600" />
              <span className="hidden sm:inline text-sm font-medium text-gray-700">
                {i18n.language === 'ar' ? 'طلباتي' : 'My Orders'}
              </span>
              <span className="sm:hidden text-sm font-medium text-gray-700">
                {i18n.language === 'ar' ? 'طلبات' : 'Orders'}
              </span>
            </button>
          }
        />
      </div>

      {/* Floating Loyalty Points Button */}
      {enableOrdering && (
        <div
          className={`${isPreview ? 'absolute' : 'fixed'} top-4 right-4 z-20 transition-opacity duration-300 ${
            showFloatingButtons ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          } ${className}`}
        >
          <CustomerLoyaltyWidget
            restaurantId={restaurantId}
            currency={currency}
            className="shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300 hover:scale-105 backdrop-blur-sm"
          />
        </div>
      )}
    </>
  );
}
